import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'
import {
  respondentBaseService,
  type AddressBookRespondent,
  type RespondentTag,
} from '@/services/respondentBaseService'

type Props = {
  open: boolean
  projectId: number
  onClose: () => void
  /** Called after a successful send with a human-readable summary line. */
  onSent: (summary: string) => void
}

/** Pick people/groups from the address book and email tracked invites for a study. */
export default function InviteRespondentsDialog({ open, projectId, onClose, onSent }: Props) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation('monitoring')

  const [respondents, setRespondents] = useState<AddressBookRespondent[] | null>(null)
  const [tags, setTags] = useState<RespondentTag[]>([])
  const [tagIds, setTagIds] = useState<number[]>([])
  const [respondentIds, setRespondentIds] = useState<number[]>([])
  const [search, setSearch] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRespondents(null)
    setTagIds([])
    setRespondentIds([])
    setSearch('')
    setError(null)
    Promise.all([respondentBaseService.listRespondents(), respondentBaseService.listTags()])
      .then(([people, tagList]) => {
        if (cancelled) return
        setRespondents(people)
        setTags(tagList)
      })
      .catch(() => {
        if (cancelled) return
        setRespondents([])
        setTags([])
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const selectedTagIds = useMemo(() => new Set(tagIds), [tagIds])
  const selectedRespondentIds = useMemo(() => new Set(respondentIds), [respondentIds])

  const recipientCount = useMemo(() => {
    const ids = new Set(respondentIds)
    for (const r of respondents ?? []) {
      if (r.tags.some((tg) => selectedTagIds.has(tg.id))) ids.add(r.id)
    }
    return ids.size
  }, [respondents, respondentIds, selectedTagIds])

  const filteredPeople = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (respondents ?? []).filter(
      (r) => !q || r.email.toLowerCase().includes(q) || r.name.toLowerCase().includes(q),
    )
  }, [respondents, search])

  const toggleTag = (id: number) =>
    setTagIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  const togglePerson = (id: number) =>
    setRespondentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))

  const handleSend = async () => {
    setSending(true)
    setError(null)
    try {
      const res = await respondentBaseService.invite(projectId, {
        respondent_ids: respondentIds,
        tag_ids: tagIds,
      })
      let summary = t('invite.result', { sent: res.sent, skipped: res.skipped })
      if (res.failed.length) summary += ' ' + t('invite.failed', { count: res.failed.length })
      onSent(summary)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('invite.error'))
      setSending(false)
    }
  }

  const loading = respondents === null
  const isEmpty = !loading && respondents.length === 0

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ fontWeight: 700 }}>{t('invite.title')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('invite.subtitle')}</Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center', py: 4 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: c.gray50, display: 'grid', placeItems: 'center' }}>
              <GroupOutlinedIcon sx={{ fontSize: 22, color: c.ink48 }} />
            </Box>
            <Typography sx={{ fontWeight: 700 }}>{t('invite.emptyTitle')}</Typography>
            <Typography sx={{ fontSize: 13.5, color: c.ink56, maxWidth: 340 }}>{t('invite.emptyBody')}</Typography>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
              onClick={() => window.open('#/respondents', '_blank', 'noopener')}
            >
              {t('invite.openBase')}
            </Button>
          </Box>
        ) : (
          <>
            {tags.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.ink48, mb: 1 }}>
                  {t('invite.groups')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags.map((tg) => {
                    const active = selectedTagIds.has(tg.id)
                    return (
                      <Chip
                        key={tg.id}
                        label={`${tg.name} · ${tg.respondent_count}`}
                        onClick={() => toggleTag(tg.id)}
                        sx={{
                          height: 30,
                          fontWeight: 600,
                          bgcolor: active ? c.coral : '#fff',
                          color: active ? '#fff' : c.ink72,
                          border: `1px solid ${active ? c.coral : c.ink08}`,
                          '&:hover': { bgcolor: active ? c.coralHover : c.gray50 },
                        }}
                      />
                    )
                  })}
                </Box>
              </Box>
            )}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1, gap: 1 }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.ink48 }}>
                  {t('invite.people')}
                </Typography>
                <TextField
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('invite.search')}
                  sx={{ width: { xs: '100%', sm: 220 }, maxWidth: '100%' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: c.ink48 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box sx={{ border: `1px solid ${c.ink08}`, borderRadius: '12px', maxHeight: 240, overflowY: 'auto' }}>
                {filteredPeople.map((r, i) => {
                  const active = selectedRespondentIds.has(r.id)
                  return (
                    <Box
                      key={r.id}
                      component="button"
                      type="button"
                      onClick={() => togglePerson(r.id)}
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        border: 'none',
                        borderTop: i === 0 ? 'none' : `1px solid ${c.ink08}`,
                        bgcolor: active ? alpha(c.coral, 0.06) : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        '&:hover': { bgcolor: active ? alpha(c.coral, 0.08) : c.gray50 },
                      }}
                    >
                      <Checkbox checked={active} size="small" sx={{ p: 0.5 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.email}
                        </Typography>
                        {r.name && <Typography sx={{ fontSize: 12, color: c.ink56 }}>{r.name}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {r.tags.slice(0, 3).map((tg) => (
                          <Chip key={tg.id} label={tg.name} size="small" sx={{ height: 20, fontSize: 11, bgcolor: alpha(c.coral, 0.1), color: c.coralHover }} />
                        ))}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            </Box>

            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: recipientCount > 0 ? c.ink : c.ink48 }}>
              {recipientCount > 0 ? t('invite.recipients', { count: recipientCount }) : t('invite.none')}
            </Typography>
            {error && <Typography sx={{ fontSize: 13, color: c.danger }}>{error}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={sending}>
          {t('invite.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSend()}
          disabled={sending || isEmpty || recipientCount === 0}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? t('invite.sending') : t('invite.send')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
